/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ShellState, UserMessageType } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { api } from '../api';
import {
  ReceiveUserInfo,
  RequestUserInfo,
  FailUserInfo,
  UserLocaleChange,
  RefreshCache,
  RegenerateAggregatedEnv,
  ReceiveRefreshCache,
  FailRefreshCache,
  RequestMessagesToUserInfo,
  ReceiveMessagesToUserInfo,
  UpdateMessageStatus,
  FixedHeaderStatus,
  RequestLoadConsents,
  ReceiveLoadConsents,
  FailLoadConsents,
  ReceiveShellConfig,
  RequestExternalUserHandlingPolicyCheck,
  ReceiveExternalUserHandlingPolicyCheck,
  FailExternalUserHandlingPolicyCheck,
  RegenerateAggregatedTables
} from '../action-types';
import { DisplayModal, RegenerateAggregatedRisk } from 'app/shell/action-types';
import { MODAL } from 'app/shell/modal-host-types';
import { SuccessNotification, ErrorNotification, InfoNotification } from 'app/infrastructure/notifications/user-messages/action-types';
import { createDescriptionSelector } from 'app/infrastructure/translate/index';
import { LayoutTranslation, layoutTranslation } from 'app/shell/constants';
import { translations } from 'app/infrastructure/translate/translations';
import { externalRedirect } from 'app/infrastructure/platform';

export const actionCreators = {
  loadUserInfo: (): ActionDispatching<any, any> => async (
    dispatch: any,
    getState: any
  ) => {
    try {
      dispatch(new RequestUserInfo());
      const userInfo = await api.userInfo();

      dispatch(new ReceiveUserInfo(userInfo));

      const loadShellConfig = async () => {
        try {
          const [
            adminUserAccess,
            toolsInfo,
            version,
            supportEmailAddress,
            surveyManagerAccess,
            helpUrl,
            manageSurveysUrl,
            feedbackUrl,
            instanceInfo
          ] = await Promise.all([
            api.userAdminAccess(),
            api.userToolsInfo(),
            api.version(),
            api.supportEmailAddress(),
            api.surveyManagerAccess(),
            api.helpUrl(),
            api.manageSurveysUrl(),
            api.feedbackUrl(),
            api.instanceInfo()
          ]);

          await dispatch(
            new ReceiveShellConfig(
              version,
              supportEmailAddress,
              adminUserAccess,
              toolsInfo,
              surveyManagerAccess,
              helpUrl,
              manageSurveysUrl,
              feedbackUrl,
              instanceInfo
            )
          );
        } catch (err) {
          dispatch(new FailUserInfo());
        }
      };

      setTimeout(loadShellConfig, 50);

    } catch (err) {
      dispatch(new FailUserInfo());
      throw err;
    }
  },

  displayModal: (name: string): any => {
    return new DisplayModal(name);
  },

  removeModal: (): any => {
    return new DisplayModal(MODAL.none);
  },

  changeUserLocale: (
    locale: string
  ): ActionDispatching<any, ApplicationState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new UserLocaleChange(locale));
    }),

  executeRefreshCache: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const shellState = getState().app as ShellState;
      const translate = createDescriptionSelector(shellState.texts);

      dispatch(new RefreshCache());

      try {
        const result = await api.refreshCache();

        if (result) {
          dispatch(actionCreators.removeModal());
          dispatch(new ReceiveRefreshCache());
          dispatch(new SuccessNotification(translate(layoutTranslation.refresh_cache_success)));
          translations.clear();
        }
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new FailRefreshCache());
      }
    },

  executeRegenerateAggregatedEnv: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const shellState = getState().app as ShellState;
      const translate = createDescriptionSelector(shellState.texts);

      dispatch(new RegenerateAggregatedEnv(true));

      try {
        const result = await api.regenerateAggregatedEnv();

        if (result) {
          dispatch(actionCreators.removeModal());
          dispatch(new RegenerateAggregatedEnv(false));
          dispatch(new SuccessNotification(translate(layoutTranslation.regenerate_aggregated_enviromental_success)));
        }
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new RegenerateAggregatedEnv(false));
      }
    },

  executeRegenerateAggregatedRisk: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const shellState = getState().app as ShellState;
      const translate = createDescriptionSelector(shellState.texts);

      dispatch(new RegenerateAggregatedRisk(true));

      try {
        const result = await api.regenerateAggregatedRisk();

        if (result) {
          dispatch(actionCreators.removeModal());
          dispatch(new RegenerateAggregatedRisk(false));
          dispatch(new SuccessNotification(translate(layoutTranslation.regenerate_aggregated_risk_success)));
        }
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new RegenerateAggregatedRisk(false));
      }
    },

  executeRegenerateAggregatedTables: (endpoint: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const shellState = getState().app as ShellState;
      const translate = createDescriptionSelector(shellState.texts);

      dispatch(new RegenerateAggregatedTables(true));

      try {
        const result = await api.regenerateAggregatedTables(endpoint);

        if (result) {
          dispatch(actionCreators.removeModal());
          dispatch(new RegenerateAggregatedTables(false));
          dispatch(new SuccessNotification(translate(layoutTranslation.regenerate_aggregated_tables_success)));
        }
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new RegenerateAggregatedTables(false));
      }
    },

  getMessagesToUser: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new RequestMessagesToUserInfo());
      const messagesToUser = await api.messagesToUser();
      dispatch(new ReceiveMessagesToUserInfo(messagesToUser));
    }),

  updateMessageStatus: (id: number, status: number) =>
    async (dispatch: any, getState: any): Promise<void> => {

      dispatch(new UpdateMessageStatus(true, id));

      try {
        await api.updateMessageStatus(id, { v: status });
        const messagesToUser = await api.messagesToUser();
        dispatch(new ReceiveMessagesToUserInfo(messagesToUser));
        dispatch(new UpdateMessageStatus(false, id));
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
        dispatch(new UpdateMessageStatus(false, id));
      }
    },
  updateFixedHeaderStatus: (fixed: boolean) =>
    (dispatch: any, getState: any) => {
      dispatch(new FixedHeaderStatus(fixed));
    },
  loadConsents: (recipientId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestLoadConsents());

      try {
        const response = await api.loadConsents(recipientId);
        dispatch(new ReceiveLoadConsents(response));
      } catch {
        dispatch(new FailLoadConsents());
      }
    },

  checkExternalUserHandlingPolicy: (redirectUri?: string): ActionDispatching<any, any> => async (dispatch: any, getState: () => ApplicationState) => {
    dispatch(new RequestExternalUserHandlingPolicyCheck());

    try {
      const externalUserHandlingPolicy = await api.checkExternalUserHandlingPolicy(redirectUri);
      externalUserHandlingPolicy?.policyConsentUrl && externalRedirect(externalUserHandlingPolicy.policyConsentUrl);
      dispatch(new ReceiveExternalUserHandlingPolicyCheck(externalUserHandlingPolicy?.accepted));
    } catch (err) {
      dispatch(new FailExternalUserHandlingPolicyCheck());
    }
  },

  showNotificationsToast: (translation: LayoutTranslation): ActionDispatching<any, any> =>
    (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new InfoNotification([{
        message: translation.notification_toast_title,
        id: 0,
        type: UserMessageType.info
      }, {
        message: translation.notification_toast_subtitle,
        id: 1,
        type: UserMessageType.info
      }]));
    }
};
